import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { useAuth } from "@/context/AuthContext";
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { useThemeColor } from "@/hooks/useThemeColor";
import { GroupAccessRequest } from "@/types/groupAccessRequest";
import { UserDocument } from "@/types/user";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
} from "react-native";
import { Invitation } from "@/types/invitation";
import { formatInviteeIdentifier } from "@/utils/formatInviteeIdentifier";
import { Ionicons } from "@expo/vector-icons";
import CustomModal from "./ui/CustomModal.web";
import { AvatarImage } from "./ui/AvatarImage";
import { AuthorName } from "./ui/AuthorName";
import { Button } from "./ui/Button";

const firestore = getFirestore(getApp());

interface GroupMembersProps {
  memberIds: string[];
  adminId: string;
  groupId: string;
}

export function GroupMembers({
  memberIds,
  adminId,
  groupId,
}: GroupMembersProps) {
  const { user } = useAuth();
  const router = useRouter();
  const [members, setMembers] = useState<UserDocument[]>([]);
  const [pendingInvites, setPendingInvites] = useState<Invitation[]>([]);
  const [pendingRequests, setPendingRequests] = useState<GroupAccessRequest[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [alertModal, setAlertModal] = useState<{
    visible: boolean;
    title: string;
    message: string;
  }>({ visible: false, title: "", message: "" });
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    title: string;
    message: string;
    confirmText: string;
    onConfirm: () => void;
  }>({
    visible: false,
    title: "",
    message: "",
    confirmText: "",
    onConfirm: () => {},
  });
  const stripesColor = useThemeColor({}, "stripes");
  const { t } = useTranslation();
  const isAdmin = user?.uid === adminId;

  const showAlert = (title: string, message: string) => {
    setAlertModal({ visible: true, title, message });
  };

  useEffect(() => {
    if (!memberIds) return;

    const fetchMembers = async () => {
      try {
        if (memberIds.length > 0) {
          const usersQuery = query(
            collection(firestore, "users"),
            where(documentId(), "in", memberIds)
          );
          const querySnapshot = await getDocs(usersQuery);
          const membersData = querySnapshot.docs.map(
            (doc) => ({ ...doc.data(), uid: doc.id } as UserDocument)
          );
          setMembers(membersData);
        } else {
          setMembers([]);
        }
      } catch (error) {
        console.error("Error fetching members: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();

    const invitesQuery = query(
      collection(firestore, "groupInvitations"),
      where("groupId", "==", groupId),
      where("status", "==", "pending")
    );
    const unsubscribeInvites = onSnapshot(invitesQuery, (snapshot) => {
      const invitesData = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as Invitation)
      );
      setPendingInvites(invitesData);
    });

    let unsubscribeRequests = () => {};
    if (isAdmin) {
      const requestsQuery = query(
        collection(firestore, "groupAccessRequests"),
        where("groupId", "==", groupId),
        where("status", "==", "pending"),
      );
      unsubscribeRequests = onSnapshot(requestsQuery, (snapshot) => {
        const requests = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() } as GroupAccessRequest),
        );
        setPendingRequests(requests);
      });
    } else {
      setPendingRequests([]);
    }

    return () => {
      unsubscribeInvites();
      unsubscribeRequests();
    };
  }, [memberIds, groupId, isAdmin]);

  const handleRemove = async (memberId: string) => {
    if (!groupId) return;
    const groupDocRef = doc(firestore, "groups", groupId);
    const userDocRef = doc(firestore, "users", memberId);

    try {
      await updateDoc(groupDocRef, { memberIds: arrayRemove(memberId) });
      await updateDoc(userDocRef, { groupIds: arrayRemove(groupId) });
      setMembers((prev) => prev.filter((m) => m.uid !== memberId));
    } catch (error) {
      console.error("Error removing member: ", error);
      showAlert(t("ERROR"), t("COULD_NOT_REMOVE_MEMBER"));
    }
  };

  const handleCancelInvite = async (invitationId: string) => {
    try {
      await deleteDoc(doc(firestore, "groupInvitations", invitationId));
    } catch (error) {
      console.error("Error cancelling invitation: ", error);
      showAlert(t("ERROR"), t("COULD_NOT_CANCEL_INVITATION"));
    }
  };

  const handleApproveRequest = async (request: GroupAccessRequest) => {
    try {
      const batch = writeBatch(firestore);
      batch.update(doc(firestore, "groups", groupId), {
        memberIds: arrayUnion(request.requesterId),
      });
      batch.update(doc(firestore, "groupAccessRequests", request.id), {
        status: "approved",
      });
      await batch.commit();
    } catch (error) {
      console.error("Error approving access request: ", error);
      showAlert(t("ERROR"), t("COULD_NOT_APPROVE_REQUEST"));
    }
  };

  const handleDeclineRequest = async (request: GroupAccessRequest) => {
    try {
      await updateDoc(doc(firestore, "groupAccessRequests", request.id), {
        status: "declined",
      });
    } catch (error) {
      console.error("Error declining access request: ", error);
      showAlert(t("ERROR"), t("COULD_NOT_DECLINE_REQUEST"));
    }
  };

  const goToProfile = (userId: string) => {
    router.push({
      pathname: "/(tabs)/public-profile",
      params: { userId },
    });
  };

  const promptRemove = (memberId: string) => {
    setConfirmModal({
      visible: true,
      title: t("REMOVE_MEMBER"),
      message: t("ARE_YOU_SURE_YOU_WANT_TO_REMOVE_MEMBER"),
      confirmText: t("REMOVE"),
      onConfirm: () => {
        setConfirmModal((prev) => ({ ...prev, visible: false }));
        handleRemove(memberId);
      },
    });
  };

  const promptLeaveGroup = () => {
    setConfirmModal({
      visible: true,
      title: t("LEAVE_GROUP"),
      message: t("ARE_YOU_SURE_YOU_WANT_TO_LEAVE_GROUP"),
      confirmText: t("LEAVE_GROUP"),
      onConfirm: () => {
        setConfirmModal((prev) => ({ ...prev, visible: false }));
        handleLeaveGroup();
      },
    });
  };

  const handleLeaveGroup = async () => {
    if (!groupId || !user) return;
    const groupDocRef = doc(firestore, "groups", groupId);
    const userDocRef = doc(firestore, "users", user.uid);

    try {
      await updateDoc(groupDocRef, { memberIds: arrayRemove(user.uid) });
      await updateDoc(userDocRef, { groupIds: arrayRemove(groupId) });
      setMembers((prev) => prev.filter((m) => m.uid !== user.uid));
    } catch (error) {
      console.error("Error leaving group: ", error);
      showAlert(t("ERROR"), t("COULD_NOT_LEAVE_GROUP"));
    }
  };

  const combinedData: (
    | (GroupAccessRequest & { type: "request" })
    | (UserDocument & { type: "member" })
    | (Invitation & { type: "invite" })
  )[] = [
    ...pendingRequests.map((r) => ({ ...r, type: "request" as const })),
    ...members.map((m) => ({ ...m, type: "member" as const })),
    ...pendingInvites.map((i) => ({ ...i, type: "invite" as const })),
  ];

  if (loading) {
    return (
      <ThemedView style={styles.centered}>
        <ActivityIndicator />
      </ThemedView>
    );
  }

  return (
    <ThemedView style={styles.container}>
      <ThemedView style={styles.sectionHeader}>
        <ThemedText style={styles.sectionLabel}>{t("MEMBERS")}</ThemedText>
      </ThemedView>
      <FlatList
        data={combinedData}
        keyExtractor={(item) => {
          if (item.type === 'member') {
            return item.uid;
          }
          return item.id;
        }}
        renderItem={({ item }) => {
          if (item.type === "request") {
            return (
              <ThemedView
                style={styles.memberItem}
                testID={`access-request-row-${item.id}`}
              >
                <AvatarImage
                  photoURL={item.requesterPhotoURL}
                  alt={item.requesterDisplayName ?? item.requesterEmail ?? t("USER")}
                  style={styles.avatar}
                  userId={item.requesterId}
                />
                <ThemedView
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TouchableOpacity
                    onPress={() => goToProfile(item.requesterId)}
                    style={{ flex: 1, marginRight: 10 }}
                  >
                    <ThemedText numberOfLines={1} ellipsizeMode="tail">
                      {item.requesterDisplayName ||
                        item.requesterEmail ||
                        t("USER")}
                    </ThemedText>
                    <ThemedText style={{ color: "gray", fontSize: 12 }}>
                      {t("PENDING_REQUEST_LABEL")}
                    </ThemedText>
                  </TouchableOpacity>
                  {isAdmin && (
                    <ThemedView style={styles.actions}>
                      <Button
                        text={t("APPROVE")}
                        onPress={() => handleApproveRequest(item)}
                        style={styles.approveButton}
                        textStyle={styles.actionButtonText}
                        testID={`access-request-approve-${item.id}`}
                      />
                      <Button
                        text={t("DECLINE")}
                        onPress={() => handleDeclineRequest(item)}
                        style={styles.declineButton}
                        textStyle={styles.actionButtonText}
                        testID={`access-request-decline-${item.id}`}
                      />
                    </ThemedView>
                  )}
                </ThemedView>
              </ThemedView>
            );
          } else if (item.type === "member") {
            return (
              <ThemedView
                style={styles.memberItem}
                testID={`member-row-${item.uid}`}
              >
                <AvatarImage
                  photoURL={item.photoURL}
                  alt={item.displayName ?? item.username ?? t("ANONYMOUS_USER")}
                  style={styles.avatar}
                  userId={item.uid}
                />
                <ThemedView
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TouchableOpacity
                    onPress={() => goToProfile(item.uid)}
                    style={{ flex: 1, marginRight: 10, minWidth: 0 }}
                  >
                    <ThemedView
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        minWidth: 0,
                      }}
                    >
                      <AuthorName
                        displayName={item.displayName}
                        username={item.username}
                        isVerified={item.isVerified}
                      />
                    </ThemedView>
                  </TouchableOpacity>
                  {item.uid === adminId && (
                    <ThemedView
                      style={[
                        styles.adminTag,
                        { backgroundColor: stripesColor },
                      ]}
                    >
                      <ThemedText style={[styles.adminTagText]}>
                        Admin
                      </ThemedText>
                    </ThemedView>
                  )}
                  {isAdmin && user?.uid !== item.uid && (
                    <ThemedView style={styles.actions}>
                      <TouchableOpacity
                        onPress={() => promptRemove(item.uid)}
                        testID={`member-remove-${item.uid}`}
                      >
                        <ThemedText style={{ color: "red" }}>
                          {t("REMOVE")}
                        </ThemedText>
                      </TouchableOpacity>
                    </ThemedView>
                  )}
                  {!isAdmin && user?.uid === item.uid && (
                    <ThemedView style={styles.actions}>
                      <TouchableOpacity
                        onPress={promptLeaveGroup}
                        testID={`member-leave-${item.uid}`}
                      >
                        <ThemedText style={{ color: "red" }}>
                          {t("LEAVE_GROUP")}
                        </ThemedText>
                      </TouchableOpacity>
                    </ThemedView>
                  )}
                </ThemedView>
              </ThemedView>
            );
          } else if (item.type === "invite") {
            return (
              <ThemedView
                style={styles.memberItem}
                testID={`invitation-row-${item.id}`}
              >
                <Image
                  source={require('../assets/images/default-profile.webp')}
                  style={styles.avatar}
                  accessibilityLabel={t("USER_AVATAR_ALT")}
                />
                <ThemedView
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <ThemedText
                    style={{ flex: 1, marginRight: 10 }}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {formatInviteeIdentifier(item, t("PENDING_INVITE"))}
                  </ThemedText>
                  <ThemedText style={{ color: "gray" }}>
                    {t("PENDING")}
                  </ThemedText>
                  {isAdmin && (
                    <ThemedView style={styles.actions}>
                      <TouchableOpacity
                        onPress={() => handleCancelInvite(item.id)}
                        accessibilityLabel={t("CANCEL_INVITATION")}
                        style={styles.cancelInviteButton}
                        testID={`invitation-cancel-${item.id}`}
                      >
                        <Ionicons name="close" size={16} color="white" />
                      </TouchableOpacity>
                    </ThemedView>
                  )}
                </ThemedView>
              </ThemedView>
            );
          }
          return null;
        }}
      />
      <CustomModal
        visible={alertModal.visible}
        onClose={() => setAlertModal({ ...alertModal, visible: false })}
        title={alertModal.title}
      >
        <ThemedText style={{ marginBottom: 20, textAlign: "center" }}>
          {alertModal.message}
        </ThemedText>
        <ThemedView style={{ flexDirection: "row", justifyContent: "center" }}>
          <Button
            text={t("OK")}
            onPress={() => setAlertModal({ ...alertModal, visible: false })}
            style={{ width: "auto", minWidth: 100 }}
          />
        </ThemedView>
      </CustomModal>
      <CustomModal
        visible={confirmModal.visible}
        onClose={() =>
          setConfirmModal((prev) => ({ ...prev, visible: false }))
        }
        title={confirmModal.title}
      >
        <ThemedText style={{ marginBottom: 20, textAlign: "center" }}>
          {confirmModal.message}
        </ThemedText>
        <ThemedView
          style={{
            flexDirection: "row",
            justifyContent: "center",
            gap: 10,
          }}
        >
          <Button
            text={t("CANCEL")}
            onPress={() =>
              setConfirmModal((prev) => ({ ...prev, visible: false }))
            }
            style={{ width: "auto", minWidth: 100, backgroundColor: "#ccc" }}
            testID="group-members-confirm-cancel"
          />
          <Button
            text={confirmModal.confirmText}
            onPress={confirmModal.onConfirm}
            style={{ width: "auto", minWidth: 100, backgroundColor: "red" }}
            testID="group-members-confirm-ok"
          />
        </ThemedView>
      </CustomModal>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  sectionHeader: {
    paddingHorizontal: 15,
    paddingTop: 15,
    paddingBottom: 10,
  },
  sectionLabel: {
    fontSize: 16,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  memberItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 15,
  },
  actions: {
    flexDirection: "row",
    marginLeft: "auto",
  },
  adminTag: {
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 10,
  },
  adminTagText: {
    fontSize: 12,
    fontWeight: "bold",
  },
  cancelInviteButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: "red",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
  },
  approveButton: {
    width: "auto",
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginLeft: 4,
    backgroundColor: "#16a34a",
  },
  declineButton: {
    width: "auto",
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginLeft: 4,
    backgroundColor: "#9ca3af",
  },
  actionButtonText: {
    fontSize: 13,
  },
});
